import {OverlayRef, ScrollStrategy} from '@angular/cdk/overlay';
import {NgZone, inject} from '@angular/core';

/**
 * Keeps a connected overlay attached to its trigger while anything on the page scrolls.
 *
 * The CDK's own reposition strategy — the default for `cdkConnectedOverlay` — repositions on
 * `ScrollDispatcher.scrolled()`, and the dispatcher only hears two things: a `scroll` on the
 * document, and elements registered by the `cdkScrollable` directive. Neither exists here.
 * `styles.scss` sets `html, body { overflow: hidden }`, so the document never scrolls; every page
 * scrolls inside its own container (`.form-content` in the manage forms, the popups' bodies, the
 * sidebar's list, the dashboards) and none of them is marked `cdkScrollable`. The dispatcher
 * therefore never emits, and an open panel keeps its original viewport position while the trigger
 * it is anchored to slides away.
 *
 * A `scroll` event does not bubble, but it *is* dispatched through the capture phase — so one
 * capture listener on the document hears every scroller in the app, whatever the markup.
 *
 * Bind it per overlay — `[cdkConnectedOverlayScrollStrategy]="scrollStrategy"` — with one instance
 * per overlay, since a strategy holds the single `OverlayRef` it was attached to. The token the
 * directive takes its default from is not exported by CDK 21, so this cannot be provided app-wide.
 */
export class RepositionOnScrollStrategy implements ScrollStrategy {
  private overlayRef: OverlayRef | null = null;
  /** Set while the listener is attached; doubles as the "enabled" flag. */
  private removeListener: (() => void) | null = null;

  constructor(private readonly zone: NgZone) {
  }

  attach(overlayRef: OverlayRef): void {
    this.overlayRef = overlayRef;
  }

  enable(): void {
    if (this.removeListener || !this.overlayRef) {
      return;
    }
    // Outside the zone: repositioning writes styles on the overlay pane and needs no change
    // detection, and a scroll fires often enough that running one per event would be costly.
    this.zone.runOutsideAngular(() => {
      const onScroll = () => this.overlayRef?.updatePosition();
      const options: AddEventListenerOptions = {capture: true, passive: true};
      document.addEventListener('scroll', onScroll, options);
      this.removeListener = () => document.removeEventListener('scroll', onScroll, options);
    });
  }

  disable(): void {
    this.removeListener?.();
    this.removeListener = null;
  }

  detach(): void {
    this.disable();
    this.overlayRef = null;
  }
}

/**
 * One strategy for one overlay. Call it in an injection context (a field initializer), which is
 * where it can reach `NgZone`:
 *
 * ```ts
 * protected readonly scrollStrategy = repositionOnScroll();
 * ```
 */
export function repositionOnScroll(): RepositionOnScrollStrategy {
  return new RepositionOnScrollStrategy(inject(NgZone));
}
